import { z } from "zod";

/* Building blocks for the API's response schemas. The Laravel API is loosely typed (ids as
   numbers or strings, booleans as 0/1, money as decimal strings), so these coerce at the edge
   and the rest of the app sees clean values. Schemas validate only the fields the UI reads;
   unknown fields pass through. */

export const zId = z.coerce.number().int();

export const zBool = z
  .union([z.boolean(), z.number(), z.string(), z.null()])
  .transform((value) => value === true || value === 1 || value === "1" || value === "true");

/** Free text that may be missing or null: always string | null. */
export const zText = z
  .string()
  .nullish()
  .transform((value) => (value === undefined || value === "" ? null : value));

/** A decimal sent as "1500000.00" or 1500000: kept as a string for exact display. */
export const zDecimal = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((value) =>
    value === undefined || value === null || value === "" ? null : String(value),
  );

/** A count or measurement that may arrive as a string. */
export const zNumber = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((value) => {
    if (value === undefined || value === null || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  });

/** An ISO timestamp or "2026-10-18 11:33:02". */
export const zTimestamp = zText;

/** A person as embedded in other records. */
export const userRefSchema = z.looseObject({
  id: zId,
  firstname: zText,
  lastname: zText,
  email: zText.optional(),
  picture_url: zText.optional(),
});
export type UserRef = z.infer<typeof userRefSchema>;

export function fullName(
  user: { firstname: string | null; lastname: string | null } | null | undefined,
): string {
  if (!user) return "";
  return [user.firstname, user.lastname].filter(Boolean).join(" ").trim();
}

/** Spatie media as serialized by the API. */
export const mediaSchema = z.looseObject({
  id: zId,
  original_url: zText,
  preview_url: zText.optional(),
  file_name: zText.optional(),
  mime_type: zText.optional(),
});
export type Media = z.infer<typeof mediaSchema>;

/** A response whose data the caller ignores (success messages). */
export const anyData = z.unknown();
